import { useMutation } from '@apollo/client';
import { PlanningActionType, usePlanning } from '@bechtel/client-common';
import { SAVE_AI_PLANNING } from '@bechtel/client-gql';
import { Text, ToastType, useToastsDispatch } from '@bechtel/gds';
import { Trans } from '@lingui/macro';
import { useCallback, useRef } from 'react';

export function useSaveAiPlanningHandler() {
  const dispatchToast = useToastsDispatch();
  const { selectedForeman, planningDispatch } = usePlanning();
  const successRef = useRef<boolean>(false);

  const [saveAiPlanning, { loading }] = useMutation(SAVE_AI_PLANNING, {
    onCompleted: async (data) => {
      if (data?.saveAiPlanning && !data.saveAiPlanning.error?.length) {
        successRef.current = true;

        // Dispatch updated foreman with ACCEPTED status to PlanningContext
        // This triggers all dependent useEffects (planned, backlog, weekStats, expired)
        // matching the same rerender flow as foreman selection change
        planningDispatch({
          type: PlanningActionType.CrewMemberSelected,
          payload: {
            isReplan: false,
            selectedForeman: {
              ...selectedForeman,
              planningAiRequest: { ...selectedForeman?.planningAiRequest, status: 'ACCEPTED' },
            },
          },
        });

        dispatchToast({ type: 'REMOVE_TOAST', payload: { id: 'ai-save-success' } });
        dispatchToast({
          type: 'ADD_TOAST',
          payload: {
            id: 'ai-save-success',
            toastType: ToastType.SUCCESS,
            content: () => (
              <Text size="500" weight="600">
                <Trans>AI planned milestones saved successfully!</Trans>
              </Text>
            ),
            autoDismissTimeout: 10000, // Auto-close after 10 seconds
          },
        });
      } else {
        successRef.current = false;
        dispatchToast({ type: 'REMOVE_TOAST', payload: { id: 'ai-save-error' } });
        dispatchToast({
          type: 'ADD_TOAST',
          payload: {
            id: 'ai-save-error',
            toastType: ToastType.WARNING,
            content: () => (
              <Text size="500" weight="600">
                <Trans>An error occurred. Please try again.</Trans>
              </Text>
            ),
            autoDismissTimeout: 10000, // Auto-close after 10 seconds
          },
        });
      }
    },
    onError: (error) => {
      successRef.current = false;
      dispatchToast({ type: 'REMOVE_TOAST', payload: { id: 'ai-save-error' } });
      dispatchToast({
        type: 'ADD_TOAST',
        payload: {
          id: 'ai-save-error',
          toastType: ToastType.WARNING,
          content: () => (
            <Text size="500" weight="600">
              <Trans>Network error has occurred. Please try again.</Trans>
            </Text>
          ),
          autoDismissTimeout: 10000, // Auto-close after 10 seconds
        },
      });
    },
  });

  const handleSaveAiPlanning = useCallback(
    async (planningAiRequestId: string): Promise<boolean> => {
      if (!planningAiRequestId) return false;

      successRef.current = false;
      try {
        await saveAiPlanning({ variables: { planningAiRequestId } });
        return successRef.current;
      } catch (error) {
        return false;
      }
    },
    [saveAiPlanning]
  );

  return { handleSaveAiPlanning, loading };
}
